import { useState } from 'react'
import { Container, Section, Skeleton } from '@radix-ui/themes'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootState } from '@AppState/redux-store'
import { PamVerificationServiceAPI } from '@API/data-services/pam-verification-service'
import { AuthScopes, PamDocumentSummary } from '@Generated/back-office-client/src'
import { PamVerificationDetailView } from './pam-verification-detail-view'
import { PamActionStatus, PamDecisionAction } from './types'

export const PamVerificationDetail = () => {
  const { activeCustomerId, currentUser } = useSelector((state: RootState) => state.users)
  const params = useParams()
  const pamPlayerId = params.pamPlayerId ?? ''
  const location = useLocation()
  const locationState = location.state as { from?: string } | null
  const queryClient = useQueryClient()
  const canWrite = currentUser?.scopes.includes(AuthScopes.writepam_verifications) ?? false

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`${activeCustomerId}-pam-verification`, pamPlayerId],
    queryFn: () => PamVerificationServiceAPI.getVerification(pamPlayerId),
    enabled: pamPlayerId.length > 0,
  })

  const decision = useMutation({
    mutationFn: ({ action, reason }: { action: PamDecisionAction; reason: string }) => {
      if (action === PamDecisionAction.APPROVE) {
        return PamVerificationServiceAPI.approve(pamPlayerId, reason)
      }
      if (action === PamDecisionAction.DENY) {
        return PamVerificationServiceAPI.deny(pamPlayerId, reason)
      }
      return PamVerificationServiceAPI.unlock(pamPlayerId, reason)
    },
    onSuccess: () => {
      refetch()
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-pam-document-queue`] })
    },
  })

  let actionStatus: PamActionStatus = PamActionStatus.IDLE
  if (decision.isPending) {
    actionStatus = PamActionStatus.PENDING
  } else if (decision.isError) {
    actionStatus = PamActionStatus.ERROR
  }

  const onDecision = async (action: PamDecisionAction, reason: string) => {
    await decision.mutateAsync({ action, reason })
  }

  const documentsQuery = useQuery({
    queryKey: [`${activeCustomerId}-pam-verification-documents`, pamPlayerId],
    queryFn: () => PamVerificationServiceAPI.listDocuments(pamPlayerId),
    enabled: pamPlayerId.length > 0,
  })

  const invalidateDocuments = () =>
    queryClient.invalidateQueries({
      queryKey: [`${activeCustomerId}-pam-verification-documents`, pamPlayerId],
    })

  const upload = useMutation({
    mutationFn: (file: File) => PamVerificationServiceAPI.uploadDocument(pamPlayerId, file),
    onSuccess: invalidateDocuments,
  })

  const remove = useMutation({
    mutationFn: (documentId: string) =>
      PamVerificationServiceAPI.deleteDocument(pamPlayerId, documentId),
    onSuccess: invalidateDocuments,
  })

  const [previewDocument, setPreviewDocument] = useState<PamDocumentSummary | null>(null)
  const previewQuery = useQuery({
    queryKey: [`${activeCustomerId}-pam-verification-document-content`, previewDocument?.id],
    queryFn: () => PamVerificationServiceAPI.getDocumentContent(pamPlayerId, previewDocument!.id),
    enabled: previewDocument !== null,
  })

  const onUpload = (file: File) => upload.mutate(file)
  const onDelete = (document: PamDocumentSummary) => {
    if (window.confirm(`Delete ${document.fileName}? This is recorded in the audit trail.`)) {
      remove.mutate(document.id)
    }
  }

  return (
    <Container>
      <Section>
        <Skeleton loading={isLoading}>
          <PamVerificationDetailView
            detail={data}
            backHref={locationState?.from}
            canWrite={canWrite}
            actionStatus={actionStatus}
            onDecision={onDecision}
            documents={documentsQuery.data ?? []}
            isUploading={upload.isPending}
            uploadError={
              upload.isError ? 'Upload failed. Check the file type and size, then retry.' : null
            }
            onUpload={onUpload}
            onDelete={onDelete}
            previewDocument={previewDocument}
            previewContent={previewQuery.data ?? null}
            previewLoading={previewQuery.isLoading}
            onPreview={setPreviewDocument}
            onClosePreview={() => setPreviewDocument(null)}
          />
        </Skeleton>
      </Section>
    </Container>
  )
}
